import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { PortalOptionModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['portalOptions']

type PortalOptionsFilters = {
  portal_id?: number,
}

interface UsePortalOptionsOptions {
  entityKey?: string,
  filters?: PortalOptionsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function usePortalOptions(options: UsePortalOptionsOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    portal_id: filterPortalId,
  } = filters

  const {
    updatedEntities: { portalOptions: portalOptionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const portalOptions = useSelector(reduxState => reduxState.entities.portalOptions)

  const filteredPortalOptions = useMemo((): PortalOptionModel[] => {
    const filtered = Object.values(portalOptions).filter((portalOption) => {
      const { portal_id } = portalOption

      const matchPortal = matchFilterNumber(portal_id, filterPortalId)

      return matchPortal
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [portalOptionsUpdatedAt, JSON.stringify(filters)])

  const filteredPortalOptionsCount = filteredPortalOptions.length
  const hasFilteredPortalOptions = !!filteredPortalOptionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPortalOptionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPortalOptions } = useReduxAction(
    'portalOptions',
    'loadPortalOptions',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPortalOptions,
    filteredPortalOptionsCount,
    hasFilteredPortalOptions,
    loading: loadingPortalOptions,
  }
}

export default usePortalOptions
